import React, { useEffect, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import CommunicationTimeline from "./index";
import {
  HistoryRow, parentTimeline, staffTimeline, studentTimeline,
} from "../../Api/communicationHistoryApi";
import { B13, B13Ink, B13TypeScale } from "../../theme/b13Tokens";

/**
 * Shared section wrapper — drop into Student, Parent, or Staff
 * profiles to render their Communication timeline. Scope enforcement
 * happens server-side; if the current viewer lacks the profile's own
 * view permission the endpoint returns 403 and this component shows
 * the error state (no data ever leaks client-side).
 */
interface Props {
  profileType: "student" | "parent" | "staff";
  profileId: number;
}

const ProfileTimelineSection: React.FC<Props> = ({ profileType, profileId }) => {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const fetcher = profileType === "student" ? studentTimeline
                  : profileType === "parent"  ? parentTimeline
                  : staffTimeline;
    fetcher(profileId)
      .then((res) => { if (alive) setRows(res.data); })
      .catch((e) => { if (alive) setError(e?.message || "Failed to load timeline."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [profileType, profileId]);

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ ...B13TypeScale.h3, mb: 0.5 }}>Communication</Typography>
        <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2 }}>
          Emails and SMS the modern audit trail has recorded for this profile.
          Legacy system messages (password reset, invoices, class reminders)
          are not surfaced here.
        </Typography>
      </Box>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <CommunicationTimeline rows={rows} loading={loading}
          emptyText="No recorded communication yet." />
      )}
    </Box>
  );
};

export default ProfileTimelineSection;
